import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Input, message } from "antd";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";

import {
  createChatFeedback,
  revokeChatFeedbackAccess,
} from "../../service/chatFeedback";

/**
 * ChatFeedbackControls — thumbs-up / thumbs-down on one assistant chat
 * answer (companion to dms_backend PR #1975).
 *
 * The chat sibling of `BriefFeedbackBar`, deliberately lighter: chat
 * answers are conversational, so the affordance is a quiet icon row
 * under the answer rather than a framed bar.
 *
 * - Thumbs-up posts immediately; a note can be added afterwards.
 * - Thumbs-down opens a compact comment box first — the backend 400s a
 *   down rating without a comment, because the comment is what makes
 *   the report fixable.
 * - A 403 on POST means the capability was revoked mid-session: the
 *   parent hides all feedback UI via `onForbidden` (same hide-on-403
 *   contract as the Brief bar).
 *
 * Visibility is the PARENT's job (`ChatPane` probes the list endpoint
 * once per session); this component assumes it may post. One row per
 * submit — there is no PATCH endpoint for chat feedback, so after a
 * successful submit the controls freeze in their submitted state.
 */

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const SentNote = styled.span`
  font-size: 12px;
  color: #059669;
`;

const CommentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 420px;
`;

const RATING_UP = "up";
const RATING_DOWN = "down";

const ChatFeedbackControls = ({ messageId, onForbidden }) => {
  // `submittedRating` is the rating the backend accepted; `pendingRating`
  // is a thumbs-down awaiting its (required) comment.
  const [submittedRating, setSubmittedRating] = useState(null);
  const [pendingRating, setPendingRating] = useState(null);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // Thumbs-up already posted; the open box adds an optional note as a
  // second row (no PATCH endpoint — the queue reads both).
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteSent, setNoteSent] = useState(false);

  const submit = async (rating, text) => {
    setIsSaving(true);
    try {
      await createChatFeedback({ messageId, rating, comment: text });
      return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        revokeChatFeedbackAccess();
        if (typeof onForbidden === "function") onForbidden();
        return false;
      }
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Couldn't send your feedback.";
      // eslint-disable-next-line no-console
      console.error("[chat-feedback] save failed:", err);
      message.error(detail, 6);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleThumbUp = async () => {
    const ok = await submit(RATING_UP, "");
    if (ok) {
      setSubmittedRating(RATING_UP);
      setPendingRating(null);
      setComment("");
    }
  };

  const handleThumbDown = () => {
    setPendingRating(RATING_DOWN);
  };

  const handleSubmitDown = async () => {
    const text = comment.trim();
    if (!text) return;
    const ok = await submit(RATING_DOWN, text);
    if (ok) {
      setSubmittedRating(RATING_DOWN);
      setPendingRating(null);
    }
  };

  const handleSubmitNote = async () => {
    const text = comment.trim();
    if (!text) return;
    const ok = await submit(RATING_UP, text);
    if (ok) {
      setNoteOpen(false);
      setNoteSent(true);
    }
  };

  const submitted = submittedRating != null;
  const downSelected =
    submittedRating === RATING_DOWN || pendingRating === RATING_DOWN;
  const commentBoxOpen = pendingRating === RATING_DOWN || noteOpen;

  return (
    <Controls data-testid="chat-feedback-controls">
      <ControlsRow>
        <Button
          size="small"
          type="text"
          icon={
            <ThumbsUp
              size={14}
              weight={submittedRating === RATING_UP ? "fill" : "regular"}
              color={submittedRating === RATING_UP ? "#059669" : "#9ca3af"}
            />
          }
          onClick={handleThumbUp}
          disabled={isSaving || submitted}
          data-testid="chat-feedback-thumb-up"
          aria-label="Good answer"
          title="Good answer"
        />
        <Button
          size="small"
          type="text"
          icon={
            <ThumbsDown
              size={14}
              weight={downSelected ? "fill" : "regular"}
              color={
                submittedRating === RATING_DOWN
                  ? "#b42318"
                  : downSelected
                    ? "#6b7280"
                    : "#9ca3af"
              }
            />
          }
          onClick={handleThumbDown}
          disabled={isSaving || submitted}
          data-testid="chat-feedback-thumb-down"
          aria-label="This is wrong"
          title="This is wrong"
        />
        {submitted && (
          <SentNote data-testid="chat-feedback-sent">
            Sent to the improvement queue
          </SentNote>
        )}
        {submittedRating === RATING_UP && !noteSent && !noteOpen && (
          <Button
            size="small"
            type="link"
            onClick={() => {
              setComment("");
              setNoteOpen(true);
            }}
            data-testid="chat-feedback-add-note"
          >
            Add a note
          </Button>
        )}
      </ControlsRow>
      {commentBoxOpen && (
        <CommentBox>
          <Input.TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={
              pendingRating === RATING_DOWN
                ? "What's wrong? Your note goes straight to the fix queue."
                : "Anything the answer got especially right?"
            }
            data-testid="chat-feedback-comment"
          />
          <ControlsRow>
            <Button
              size="small"
              type="primary"
              onClick={
                pendingRating === RATING_DOWN
                  ? handleSubmitDown
                  : handleSubmitNote
              }
              loading={isSaving}
              disabled={!comment.trim()}
              data-testid="chat-feedback-submit"
            >
              Submit
            </Button>
            <Button
              size="small"
              onClick={() => {
                setPendingRating(null);
                setNoteOpen(false);
                setComment("");
              }}
              disabled={isSaving}
              data-testid="chat-feedback-cancel"
            >
              Cancel
            </Button>
          </ControlsRow>
        </CommentBox>
      )}
    </Controls>
  );
};

ChatFeedbackControls.propTypes = {
  // Backend MatterChatMessage id — from thread hydration or the
  // post-turn refetch in ChatPane.
  messageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onForbidden: PropTypes.func,
};

ChatFeedbackControls.defaultProps = {
  onForbidden: null,
};

export default ChatFeedbackControls;
